import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportSvodRasFilterOptions } from "../svod-ras";
import { ReportSvodRasFilterSettingsForm } from "../settings-form/ReportSvodRasFilterSettingsForm";
import { ReportSvodRasFilterSettings } from "../settings/ReportSvodRasFilterSettings";
import { ReportSvodRasRow } from "../table-data/ReportSvodRasRow";
import { ReportSvodRasJournalRequest } from "../journal/ReportSvodRasJournalRequest";
import { ReportSvodRasTableDataRequest } from "../data-request/ReportSvodRasTableDataRequest";

export class ReportSvodRasFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportSvodRasFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидации
        if (settings.reportName !== 'SVOD-RAS') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'SVOD-RAS') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let dataSource2 = options.dataSources2.find(it => it.value === settings.dataSource2)
        if (!dataSource2) {
            throw new Error('error_not_found_data_source')
        }
        dataSource2 = { ...dataSource2 }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const segmentations = settings.segmentations ?? cloneDeep(reportSvodRasFilterOptions.segmentations)

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let byFormOption = options.byForms.find(it => it.value === settings.byForm)
        if (!byFormOption) {
            throw new Error('error_not_found_by_form')
        }
        byFormOption = { ...byFormOption }

        let isRoundUp = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const developTypeOption = cloneDeep(options.developTypes.find(it => it.value === settings.developType) ?? null)

        // 2. Формирование
        const form: ReportSvodRasFilterSettingsForm = {
            // Base:
            key: 'SVOD-RAS',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            dataSource2: dataSource2,
            date: new Date(settings.date),
            region: regionOption,
            budgetTypes: budgetTypeOptions,
            segmentations: segmentations,
            expenses: cloneDeep(settings.expenses),
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            statuses: statusOptions,
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            comparePrevYear: settings.comparePrevYear,
            regionsTotal: settings.regionsTotal,
            isTree: settings.isTree,
            fillCbc: settings.fillCbc,
            kbkThisYearOnly: settings.kbkThisYearOnly,
            developType: developTypeOption,
            totalFinSource: settings.totalFinSource,
            byForm: byFormOption,
            indicators: cloneDeep(settings.indicators),
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'SVOD-RAS') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.byForm) {
            throw new Error('error_input_by_form')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type')
        }

        // RQ.09/BUG-8: та же проверка, что и в constructReport() ниже - без неё шаблон с
        // разрезностью КБК и без единого выбранного уровня классификатора расходов уходил
        // на сервер сохранения шаблонов необнаруженным (constructReport проверяет это только
        // для самой генерации отчёта, formToSettings использует также сохранение шаблона).
        const cutnessForValidation = form.segmentations?.filter(it => it.active).map(it => it.value) ?? []
        const activeExpensesForValidation = form.expenses.filter(item => item.active)
        if (cutnessForValidation.includes('KBK') && activeExpensesForValidation.length === 0) {
            throw new Error('error_input_at_least_one_kbk')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const segmentations = form.segmentations ?? cloneDeep(reportSvodRasFilterOptions.segmentations)

        const settings: ReportSvodRasFilterSettings = {
            // Base:
            reportName: 'SVOD-RAS',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            dataSource2: form.dataSource2.value,
            date: strDate,
            region: form.region.value,
            budgetTypes: form.budgetTypes.map(it => it.value),
            segmentations: segmentations,
            expenses: cloneDeep(form.expenses),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            statuses: form.statuses.map(it => it.value),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            comparePrevYear: form.comparePrevYear,
            regionsTotal: form.regionsTotal,
            isTree: form.isTree,
            fillCbc: form.fillCbc ?? false,
            kbkThisYearOnly: form.kbkThisYearOnly,
            developType: form.developType.value ?? null,
            totalFinSource: form.totalFinSource,
            byForm: form.byForm.value,
            indicators: cloneDeep(form.indicators),
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportSvodRasFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportSvodRasFilterSettingsForm = {
            key: 'SVOD-RAS',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'SVOD-RAS')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'EXPENSES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields
            dataSource2: options.dataSources2.find(it => it.value === 'E-AKIMAT')!,
            date: yesterdayDate,
            region: null,
            budgetTypes: [],
            // Разрезность по умолчанию пуста (RQ.09) - плейсхолдер «Выберите разрезность»
            segmentations: cloneDeep(options.segmentations).map(it => ({ ...it, active: false })),
            expenses: cloneDeep(options.expenses),
            mutuallyRedeeming: null,
            mutuallyRedeemingLevels: cloneDeep(options.mutuallyRedeemingLevels),
            statuses: options.statuses.filter(it => it.value === '39'),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            comparePrevYear: true,
            regionsTotal: false,
            isTree: true,
            fillCbc: false,
            kbkThisYearOnly: true,
            developType: options.developTypes.find(it => it.value === null)!,
            totalFinSource: false,
            byForm: options.byForms.find(it => it.value === '4-20')!,
            indicators: cloneDeep(options.indicators),
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидации и формирование данных для таблицы
        if (form.key !== 'SVOD-RAS') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_input_budget_types')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const activeExpenses = form.expenses.filter(item => item.active)
        const classifications: string[] = activeExpenses.map(it => it.key)

        // RQ.09: если в разрезах выбран КБК, должен быть выбран хотя бы один уровень классификатора расходов
        if (cutness.includes('KBK') && classifications.length === 0) {
            throw new Error('error_input_at_least_one_kbk')
        }

        const vzkList = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })

        if (form.developType == null) {
            throw new Error('error_input_develop_type')
        }
        const developType = form.developType.value

        const params: ReportSvodRasTableDataRequest = {
            user_id: userId,
            expenses: form.expenses,
            source: form.dataSource.value,
            source2: form.dataSource2.value,
            repdate: strDate,
            region: form.region.value,
            cutness: cutness,
            mutually_redeeming: form.mutuallyRedeeming.value,
            vzk_list: vzkList,
            budget_types: form.budgetTypes.map(it => it.value),
            status_list: form.statuses.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            locale: i18nService.locale,
            tree_view: form.isTree,
            form: form.byForm.value,
            fill_cbc: form.fillCbc,
            develop_type: developType,
            total_fin_source: form.totalFinSource,
            kbk_this_year_only: form.kbkThisYearOnly,
            compare_prev_year: form.comparePrevYear,
            regions_total: form.regionsTotal,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/svod-ras',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: any[] = await response.json()
        const firstClsn = classifications[0]
        const lastClsn = classifications[classifications.length - 1]
        const idMap: any = {}
        for (let i = 0; i < form.segmentations?.length; i++) {
            idMap[form.segmentations[i].value] = i
        }
        const tableData: ReportSvodRasRow[] = responseTableData.map((row: any, index) => {
            row.index = index

            // RQ.eAk.SvodRas: в древовидном режиме Регион/ГУ из «Разрезности» выводятся
            // отдельными колонками (см. report-svod-ras.vue::buildColumnOrder) - значение
            // row.region/row.gu там используется как есть, затирать классификатор «Расходы»
            // не нужно. Легаси-поведение (затирание первой/последней колонки) сохраняем
            // только для нетабличного (isTree=false) вида, где значение просто подмешивается
            // в текст наименования строки.
            if (!form.isTree) {
                if (form.segmentations) {
                    if (row.region) {
                        if (idMap['REGION'] > idMap['KBK']) {
                            row[lastClsn] = row.region
                        } else {
                            row[firstClsn] = row.region
                        }
                    }
                    if (row.gu) {
                        if (idMap['GU'] > idMap['KBK']) {
                            row[lastClsn] = row.gu
                        } else {
                            row[firstClsn] = row.gu
                        }
                    }
                }

                if (row[lastClsn]) {
                    if (typeof row.name === 'string' && row.name) {
                        row.name = `${row[lastClsn]} - ${row.name}`
                    } else {
                        row.name = row[lastClsn]
                    }
                }
            }

            return row
        })

        // 2. Формирование параметров для выгрузки в журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)
        const journalParams: ReportSvodRasJournalRequest = {
            reportName: "SVOD-RAS",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            columns_list: classifications,
            indicators_order: indicatorsOrder,
            cutness: cutness,
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
